import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const STORAGE_KEY = "field_places_filter"

// Connects to data-controller="place-filter"
// Self-contained: manages its own sheet + overlay targets so it doesn't
// conflict with the sort bottom-sheet controller on the same element.
//
// Two modes:
//   - Places: the named retailer / place owner / visit frequency inputs, with the
//     choice remembered in localStorage and re-applied on the next visit.
//   - Generic (Activity, card #4070): radio inputs marked as "paramInput" targets,
//     each carrying data-param="<query param>". The choice lives only in the query
//     string (persist=false), is applied with a Turbo Frame visit that advances the
//     URL (advance=true), keeps every other param (date, week, view, search text),
//     and the radios re-sync from the URL each time the drawer opens.
export default class extends Controller {
  static targets = [
    "filterSheet", "filterOverlay",
    "badge", "retailerInput", "placeOwnerInput", "visitFrequencyInput", "paramInput"
  ]
  static values = { frame: String, persist: { type: Boolean, default: true }, advance: { type: Boolean, default: false } }

  connect() {
    // Set initial sheet state (off-screen, no transition)
    this.filterSheetTarget.style.transform         = "translateY(100%)"
    this.filterOverlayTarget.style.opacity         = "0"
    this.filterOverlayTarget.style.pointerEvents   = "none"
    this.filterOverlayTarget.style.backgroundColor = "rgba(0,0,0,0.6)"

    requestAnimationFrame(() => {
      this.filterSheetTarget.style.transition   = "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)"
      this.filterOverlayTarget.style.transition = "opacity 0.25s ease"
    })

    this._bindDrag()

    // Generic mode: keep the badge honest after any frame navigation (chip link, day tap).
    if (!this.persistValue) {
      this._frameLoaded = () => setTimeout(() => this.updateBadge(), 0)
      document.getElementById(this.frameValue)?.addEventListener("turbo:frame-load", this._frameLoaded)
    }

    const saved = this.persistValue ? this.loadPreference() : null
    if (saved) {
      const url = new URL(window.location.href)
      const alreadyFiltered = url.searchParams.get("retailer_id") ||
                              url.searchParams.get("place_owner_id") ||
                              url.searchParams.get("visit_frequency")
      if (!alreadyFiltered && (saved.retailerId || saved.placeOwnerId || saved.visitFrequency)) {
        this.applyFilters(saved.retailerId, saved.placeOwnerId, saved.visitFrequency)
        return
      }
    }
    this.updateBadge()
  }

  disconnect() {
    if (this._frameLoaded) document.getElementById(this.frameValue)?.removeEventListener("turbo:frame-load", this._frameLoaded)
    this._unbindDrag()
  }

  openSheet() {
    this.syncParamInputs()
    this._transitionsOn()
    this.filterOverlayTarget.style.pointerEvents = "auto"
    this.filterOverlayTarget.style.opacity       = "1"
    this.filterSheetTarget.style.transform       = "translateY(0)"
  }

  closeSheet() {
    this._transitionsOn()
    this.filterOverlayTarget.style.opacity       = "0"
    this.filterOverlayTarget.style.pointerEvents = "none"
    this.filterSheetTarget.style.transform       = "translateY(100%)"
  }

  apply() {
    if (this.hasParamInputTarget) return this.applyParams()

    const retailerId     = this.retailerInputTargets.find(el => el.checked)?.value      || ""
    const placeOwnerId   = this.placeOwnerInputTargets.find(el => el.checked)?.value    || ""
    const visitFrequency = this.visitFrequencyInputTargets.find(el => el.checked)?.value || ""

    this.savePreference({ retailerId, placeOwnerId, visitFrequency })
    this.closeSheet()
    this.applyFilters(retailerId, placeOwnerId, visitFrequency)
  }

  clearAll() {
    if (this.hasParamInputTarget) return this.clearParams()

    this.savePreference({ retailerId: "", placeOwnerId: "", visitFrequency: "" })
    this.closeSheet()
    this.applyFilters("", "", "")
  }

  applyFilters(retailerId, placeOwnerId, visitFrequency) {
    const frame = document.getElementById(this.frameValue)
    if (!frame) return

    const url = new URL(window.location.href)
    retailerId     ? url.searchParams.set("retailer_id",     retailerId)     : url.searchParams.delete("retailer_id")
    placeOwnerId   ? url.searchParams.set("place_owner_id",  placeOwnerId)   : url.searchParams.delete("place_owner_id")
    visitFrequency ? url.searchParams.set("visit_frequency", visitFrequency) : url.searchParams.delete("visit_frequency")

    frame.src = url.toString()
    this.updateBadge()
  }

  // Generic mode ----------------------------------------------------

  get paramNames() {
    return [...new Set(this.paramInputTargets.map(el => el.dataset.param))]
  }

  applyParams() {
    const url = new URL(window.location.href)
    this.paramNames.forEach(name => {
      const value = this.paramInputTargets.find(el => el.dataset.param === name && el.checked)?.value || ""
      value ? url.searchParams.set(name, value) : url.searchParams.delete(name)
    })
    this.closeSheet()
    this.visitFrame(url)
  }

  clearParams() {
    const url = new URL(window.location.href)
    this.paramNames.forEach(name => url.searchParams.delete(name))
    this.closeSheet()
    this.visitFrame(url)
  }

  // Radios follow the address bar, so a filter cleared elsewhere (the chip link)
  // never comes back from a stale radio.
  syncParamInputs() {
    if (!this.hasParamInputTarget) return
    const params = new URL(window.location.href).searchParams
    this.paramInputTargets.forEach(el => {
      el.checked = el.value === (params.get(el.dataset.param) || "")
    })
  }

  visitFrame(url) {
    url.searchParams.delete("page")
    if (this.advanceValue) {
      Turbo.visit(url.toString(), { frame: this.frameValue, action: "advance" })
    } else {
      const frame = document.getElementById(this.frameValue)
      if (frame) frame.src = url.toString()
    }
    this.updateBadge(url)
  }

  updateBadge(url = new URL(window.location.href)) {
    if (this.hasParamInputTarget) {
      const active = this.paramNames.some(name => url.searchParams.get(name))
      if (this.hasBadgeTarget) this.badgeTarget.classList.toggle("hidden", !active)
      return
    }
    const hasFilter = url.searchParams.get("retailer_id") ||
                      url.searchParams.get("place_owner_id") ||
                      url.searchParams.get("visit_frequency")
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.toggle("hidden", !hasFilter)
    }
  }

  // ── Drag-to-dismiss ────────────────────────────────────────────────

  _bindDrag() {
    this._onTouchStart = this._touchStart.bind(this)
    this._onTouchMove  = this._touchMove.bind(this)
    this._onTouchEnd   = this._touchEnd.bind(this)
    this.filterSheetTarget.addEventListener("touchstart", this._onTouchStart, { passive: true })
    this.filterSheetTarget.addEventListener("touchmove",  this._onTouchMove,  { passive: false })
    this.filterSheetTarget.addEventListener("touchend",   this._onTouchEnd,   { passive: true })
  }

  _unbindDrag() {
    this.filterSheetTarget.removeEventListener("touchstart", this._onTouchStart)
    this.filterSheetTarget.removeEventListener("touchmove",  this._onTouchMove)
    this.filterSheetTarget.removeEventListener("touchend",   this._onTouchEnd)
  }

  _touchStart(e) {
    this._dragStartY   = e.touches[0].clientY
    this._dragLastY    = e.touches[0].clientY
    this._dragLastTime = Date.now()
    this._dragVelocity = 0
    this._dragging     = true
    this._transitionsOff()
  }

  _touchMove(e) {
    if (!this._dragging) return
    const y      = e.touches[0].clientY
    const delta  = y - this._dragStartY
    const clamped = delta > 0 ? delta : delta * 0.1
    const now    = Date.now()
    this._dragVelocity = (y - this._dragLastY) / (now - this._dragLastTime || 1)
    this._dragLastY    = y
    this._dragLastTime = now
    this.filterSheetTarget.style.transform = `translateY(${clamped}px)`
    const progress = Math.min(Math.max(clamped / (this.filterSheetTarget.offsetHeight || 400), 0), 1)
    this.filterOverlayTarget.style.opacity = String(1 - progress)
    if (delta > 0) e.preventDefault()
  }

  _touchEnd() {
    if (!this._dragging) return
    this._dragging = false
    const sheetHeight   = this.filterSheetTarget.offsetHeight || 400
    const currentY      = new DOMMatrixReadOnly(getComputedStyle(this.filterSheetTarget).transform).m42
    const pastThreshold = currentY > sheetHeight * 0.4
    const fastFlick     = this._dragVelocity > 0.5
    this._transitionsOn()
    if (pastThreshold || fastFlick) {
      this.closeSheet()
    } else {
      this.filterSheetTarget.style.transform = "translateY(0)"
      this.filterOverlayTarget.style.opacity = "1"
    }
  }

  _transitionsOn() {
    this.filterSheetTarget.style.transition   = "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)"
    this.filterOverlayTarget.style.transition = "opacity 0.25s ease"
  }

  _transitionsOff() {
    this.filterSheetTarget.style.transition   = "none"
    this.filterOverlayTarget.style.transition = "none"
  }

  savePreference(data) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }

  loadPreference() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) }
    catch { return null }
  }
}
